import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";
import { useTranslation } from "react-i18next";
import MainModal from "components/MainModal/MainModal.view";
import { LocationsContext } from "contexts/LocationsContext/LocationsContext";
import { UserContext } from "contexts/UserContext/UserContext";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { ISegmentFieldValues } from "../SegmentFields";
import { ISplitDowntimeModalProps, ISplitSegmentState } from "./SplitDowntimeModalInterfaces";
import {
    buildSplitMutationVariables,
    canAddSegment,
    createAddedSegment,
    createInitialSegments,
    getNextAvailableStartIso,
    getSegmentStartBounds,
    validateSplitSegments,
} from "./splitDowntimeHelpers";
import { SplitSegmentRow } from "./SplitSegmentRow";
import { useStyles } from "./SplitDowntimeModal.styles";

export const SplitDowntimeModal = ({ isOpen, row, onClose, onSplit }: ISplitDowntimeModalProps) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { millTimezone } = useContext(LocationsContext);
    const { user_id } = useContext(UserContext);
    const { defaultFailureTypes } = FailureDetailsEntity.GetDefaultFailureTypes();
    const { splitEventReportMutation, loading } = EventReportsEntity.SplitEventReportBO();

    const [segments, setSegments] = useState<ISplitSegmentState[]>(() => createInitialSegments(row));
    const [submitError, setSubmitError] = useState<boolean>(false);
    const segmentCounter = useRef<number>(0);

    // Reset the segment list whenever the modal (re)opens for a row.
    useEffect(() => {
        if (isOpen) {
            segmentCounter.current = 0;
            setSegments(createInitialSegments(row));
            setSubmitError(false);
        }
    }, [isOpen, row]);

    const originalStartIso = useMemo(() => moment.utc(row.start_date).toISOString(), [row.start_date]);
    const originalEndIso = useMemo(() => moment.utc(row.end_date).toISOString(), [row.end_date]);
    const tz = millTimezone || "UTC";

    const validation = useMemo(() => validateSplitSegments(segments, row), [segments, row]);

    const handleFieldsChange = (key: string, fields: ISegmentFieldValues) =>
        setSegments((prev) => prev.map((segment) => (segment.key === key ? { ...segment, fields } : segment)));

    const handleStartChange = (key: string, iso: string | null) =>
        setSegments((prev) => prev.map((segment) => (segment.key === key ? { ...segment, start: iso } : segment)));

    const canAdd = useMemo(() => canAddSegment(segments, row), [segments, row]);

    const handleAddSegment = () => {
        if (!canAddSegment(segments, row)) return;
        const nextStart = getNextAvailableStartIso(segments, row);
        segmentCounter.current += 1;
        setSegments((prev) => [...prev, createAddedSegment(`segment-${segmentCounter.current}`, nextStart)]);
    };

    const handleRemoveSegment = (key: string) =>
        setSegments((prev) => prev.filter((segment) => segment.key !== key));

    const handleConfirm = async () => {
        if (!validation.valid) return;
        setSubmitError(false);
        try {
            const { eventReportId, originalChanges, segments: segmentInserts } = buildSplitMutationVariables(
                row,
                segments,
                user_id ?? undefined,
            );
            // Spread the ascending segment inserts into $seg0..$seg{n-1} for the aliased
            // insert_event_report_one fields (separate statements, one transaction).
            const variables: Record<string, unknown> = { eventReportId, originalChanges };
            segmentInserts.forEach((segmentInsert, index) => {
                variables[`seg${index}`] = segmentInsert;
            });
            await splitEventReportMutation({ variables, segmentCount: segmentInserts.length });
            await onSplit();
            onClose();
        } catch {
            setSubmitError(true);
        }
    };

    const errorMessage = submitError
        ? t("locations.mill_dashboards.details.split.error_generic")
        : validation.errorKey
        ? t(`locations.mill_dashboards.details.split.validation_${validation.errorKey}`)
        : "";

    return (
        <MainModal
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton
            title={t("locations.mill_dashboards.details.split.modal_title")}
            okButton={t("locations.mill_dashboards.details.split.confirm")}
            disableOkButton={!validation.valid || loading}
            disableCancelButton={loading}
            onOkButtonClick={handleConfirm}
            onCancelButtonClick={onClose}
        >
            <div className={classes.spanHeader}>
                <Typography className={classes.formLabel}>
                    {t("locations.mill_dashboards.details.split.original_span")}
                </Typography>
                <Typography className={classes.spanValue}>
                    {`${moment.utc(originalStartIso).tz(tz).format("MMM D, YYYY h:mm A")} → ${moment
                        .utc(originalEndIso)
                        .tz(tz)
                        .format("MMM D, YYYY h:mm A")}`}
                </Typography>
            </div>

            <div className={classes.content}>
                {segments.map((segment, index) => {
                    const bounds = getSegmentStartBounds(segments, index, row);
                    return (
                        <SplitSegmentRow
                            key={segment.key}
                            segment={segment}
                            index={index}
                            row={row}
                            defaultFailureTypes={defaultFailureTypes}
                            millTimezone={millTimezone}
                            minStartIso={bounds.minIso}
                            maxStartIso={bounds.maxIso}
                            onFieldsChange={handleFieldsChange}
                            onStartChange={handleStartChange}
                            onRemove={handleRemoveSegment}
                        />
                    );
                })}

                <div className={classes.addSegmentRow}>
                    <Button
                        className={classes.addSegmentButton}
                        color="info"
                        startIcon={<AddIcon />}
                        onClick={handleAddSegment}
                        disabled={loading || !canAdd}
                    >
                        {t("locations.mill_dashboards.details.split.add_segment")}
                    </Button>
                </div>
            </div>

            <Typography className={classes.errorText}>{errorMessage}</Typography>
        </MainModal>
    );
};
